<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsappRecontactAPI from 'dashboard/api/whatsappRecontact';
import WhatsappRecontactDialog from './WhatsappRecontactDialog.vue';
import WhatsappRecontactHelp from './WhatsappRecontactHelp.vue';
import {
  formatInstant,
  padTime,
  RECONTACT_STATUSES,
  recontactStatusLabel,
  weekdayLabel,
} from '../helpers/whatsappRecontactTime';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const schedules = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const editing = ref(null);
const pendingDelete = ref(null);
const ruleDialog = ref(null);
const deleteDialog = ref(null);

const timeZone = computed(() => props.inbox.timezone || 'UTC');
const templates = computed(
  () => getFilteredWhatsAppTemplates.value(props.inbox.id) || []
);

const clockText = (wday, hour, minute) =>
  t('INBOX_MGMT.RECONTACT.CLOCK', {
    day: weekdayLabel(wday, t),
    time: padTime(hour, minute),
  });

const scheduleRows = schedule => {
  const isOneShot = schedule.schedule_kind === 'one_shot';
  const moment = (instant, wday, hour, minute) =>
    isOneShot
      ? formatInstant(instant, timeZone.value)
      : clockText(wday, hour, minute);

  return [
    {
      key: 'send',
      label: t('INBOX_MGMT.RECONTACT.SUMMARY.SEND'),
      value: moment(
        schedule.send_at,
        schedule.send_wday,
        schedule.send_hour,
        schedule.send_minute
      ),
    },
    {
      key: 'from',
      label: t('INBOX_MGMT.RECONTACT.SUMMARY.FROM'),
      value: moment(
        schedule.window_starts_at,
        schedule.window_start_wday,
        schedule.window_start_hour,
        schedule.window_start_minute
      ),
    },
    {
      key: 'until',
      label: t('INBOX_MGMT.RECONTACT.SUMMARY.UNTIL'),
      value: moment(
        schedule.window_ends_at,
        schedule.window_end_wday,
        schedule.window_end_hour,
        schedule.window_end_minute
      ),
    },
  ];
};

const audienceText = schedule => {
  const rules = [
    schedule.require_assignee &&
      t('INBOX_MGMT.RECONTACT.AUDIENCE.ASSIGNEE_SHORT'),
    schedule.require_closed_window &&
      t('INBOX_MGMT.RECONTACT.AUDIENCE.CLOSED_WINDOW_SHORT'),
    schedule.require_unanswered &&
      t('INBOX_MGMT.RECONTACT.AUDIENCE.UNANSWERED_SHORT'),
  ].filter(Boolean);
  const statuses = RECONTACT_STATUSES.filter(status =>
    (schedule.eligible_statuses || []).includes(status)
  ).map(status => recontactStatusLabel(status, t));
  return [...rules, statuses.join(', ')].filter(Boolean).join(' · ');
};

const isMarketing = schedule =>
  String(schedule.template_params?.category || '').toUpperCase() ===
  'MARKETING';

const load = async () => {
  isLoading.value = true;
  try {
    const response = await WhatsappRecontactAPI.list(props.inbox.id);
    schedules.value = response.data;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX_MGMT.RECONTACT.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openRuleDialog = async schedule => {
  editing.value = schedule;
  await nextTick();
  ruleDialog.value?.open();
};

const save = async payload => {
  isSaving.value = true;
  const { headerFile, ...schedule } = payload;
  try {
    if (editing.value) {
      await WhatsappRecontactAPI.update(
        props.inbox.id,
        editing.value.id,
        schedule,
        headerFile
      );
    } else {
      await WhatsappRecontactAPI.create(props.inbox.id, schedule, headerFile);
    }
    useAlert(t('INBOX_MGMT.RECONTACT.SAVED'));
    ruleDialog.value?.close();
    await load();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('INBOX_MGMT.RECONTACT.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const toggle = async schedule => {
  try {
    await WhatsappRecontactAPI.update(props.inbox.id, schedule.id, {
      enabled: !schedule.enabled,
    });
    await load();
  } catch (error) {
    useAlert(error?.response?.data?.message || t('INBOX_MGMT.RECONTACT.ERROR'));
  }
};

const askToDelete = schedule => {
  pendingDelete.value = schedule;
  deleteDialog.value?.open();
};

const confirmDelete = async () => {
  if (!pendingDelete.value) return;

  isDeleting.value = true;
  try {
    await WhatsappRecontactAPI.delete(props.inbox.id, pendingDelete.value.id);
    useAlert(t('INBOX_MGMT.RECONTACT.DELETED'));
    deleteDialog.value?.close();
    await load();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX_MGMT.RECONTACT.ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

const runText = run =>
  t('INBOX_MGMT.RECONTACT.LAST_RUN', {
    sent: run.sent_count,
    skipped: run.skipped_count,
    failed: run.failed_count,
  });

onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0 flex-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.RECONTACT.TITLE') }}
        </h3>
        <p class="mb-0 mt-1 text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.RECONTACT.DESCRIPTION') }}
        </p>
      </div>
      <WhatsappRecontactHelp />
    </div>
    <Spinner v-if="isLoading" />
    <p v-else-if="!schedules.length" class="mb-0 text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.RECONTACT.EMPTY') }}
    </p>
    <ul v-else class="flex flex-col gap-3 p-0 m-0 list-none">
      <li
        v-for="schedule in schedules"
        :key="schedule.id"
        class="flex flex-col gap-3 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <p class="mb-0 min-w-0 text-sm font-medium text-n-slate-12">
            {{ schedule.template_params?.name }}
            <span class="font-normal text-n-slate-11">
              {{
                schedule.enabled
                  ? t('INBOX_MGMT.RECONTACT.ON')
                  : t('INBOX_MGMT.RECONTACT.OFF')
              }}
            </span>
          </p>
          <span class="shrink-0 text-sm text-n-slate-11">{{ timeZone }}</span>
        </div>
        <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          <template v-for="row in scheduleRows(schedule)" :key="row.key">
            <dt class="text-n-slate-11">{{ row.label }}</dt>
            <dd class="m-0 text-n-slate-12">{{ row.value }}</dd>
          </template>
        </dl>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ audienceText(schedule) }}
        </p>
        <p v-if="isMarketing(schedule)" class="mb-0 text-sm text-n-amber-11">
          {{ t('INBOX_MGMT.RECONTACT.FORM.MARKETING') }}
        </p>
        <p v-if="schedule.latest_run" class="mb-0 text-sm text-n-slate-11">
          {{ runText(schedule.latest_run) }}
        </p>
        <div class="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="faded"
            color="slate"
            :label="t('INBOX_MGMT.RECONTACT.EDIT')"
            @click="openRuleDialog(schedule)"
          />
          <Button
            size="sm"
            variant="faded"
            color="slate"
            :label="
              schedule.enabled
                ? t('INBOX_MGMT.RECONTACT.TURN_OFF')
                : t('INBOX_MGMT.RECONTACT.TURN_ON')
            "
            @click="toggle(schedule)"
          />
          <Button
            size="sm"
            variant="faded"
            color="ruby"
            :label="t('INBOX_MGMT.RECONTACT.DELETE')"
            @click="askToDelete(schedule)"
          />
        </div>
      </li>
    </ul>
    <Button
      v-if="!isLoading"
      :label="t('INBOX_MGMT.RECONTACT.ADD')"
      @click="openRuleDialog(null)"
    />
    <WhatsappRecontactDialog
      ref="ruleDialog"
      :time-zone="timeZone"
      :templates="templates"
      :schedule="editing"
      :is-saving="isSaving"
      @save="save"
    />
    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="t('INBOX_MGMT.RECONTACT.DELETE_TITLE')"
      :description="t('INBOX_MGMT.RECONTACT.DELETE_DESCRIPTION')"
      :confirm-button-label="t('INBOX_MGMT.RECONTACT.DELETE_CONFIRM')"
      :cancel-button-label="t('INBOX_MGMT.RECONTACT.FORM.CANCEL')"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
    />
  </div>
</template>
